import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import {
  setUserContext,
  setTag,
  setContext,
  addBreadcrumb,
  logInfo,
} from "../shared/utils/logger.js";

const SentryContext = createContext();

/**
 * Sentry Context Provider
 * Manages user context, performance monitoring, and error tracking
 */
export const SentryProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // Set user context when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      setUserContext({
        id: user.id || user._id,
        email: user.email,
        username: user.username || user.name,
        role: user.role,
        permissions: user.permissions || [],
      });

      // Set user-specific tags
      setTag("user_id", user.id || user._id);
      setTag("user_role", user.role);
      setTag("user_authenticated", true);

      // Set user context
      setContext("user", {
        id: user.id || user._id,
        email: user.email,
        username: user.username || user.name,
        role: user.role,
        permissions: user.permissions || [],
        lastLogin: user.lastLogin,
        createdAt: user.createdAt,
      });

      logInfo("Sentry user context updated", {
        userId: user.id || user._id,
        role: user.role,
      });
    } else {
      // Clear user context when logged out
      setUserContext(null);
      setTag("user_authenticated", false);
      setContext("user", null);
    }
  }, [user, isAuthenticated]);

  // Set application context
  useEffect(() => {
    setContext("application", {
      name: "Bubakii Logistics Platform",
      version: "1.0.0",
      environment: import.meta.env.MODE,
      buildTime: new Date().toISOString(),
    });

    setTag("application", "bubakii-logistics");
    setTag("environment", import.meta.env.MODE);
    setTag("version", "1.0.0");
  }, []);

  // Track page views and navigation
  const trackPageView = useCallback((pageName, additionalData = {}) => {
    addBreadcrumb({
      message: `Page view: ${pageName}`,
      category: "navigation",
      level: "info",
      data: {
        page: pageName,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...additionalData,
      },
    });

    setContext("currentPage", {
      name: pageName,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...additionalData,
    });

    logInfo("Page view tracked", { page: pageName, url: window.location.href });
  }, []);

  // Track user actions
  const trackUserAction = useCallback(
    (action, data = {}) => {
      addBreadcrumb({
        message: `User action: ${action}`,
        category: "user",
        level: "info",
        data: {
          action,
          timestamp: new Date().toISOString(),
          userId: user?.id || user?._id,
          ...data,
        },
      });

      logInfo("User action tracked", {
        action,
        userId: user?.id || user?._id,
        ...data,
      });
    },
    [user]
  );

  // Track API calls
  const trackApiCall = useCallback(
    (endpoint, method, status, duration, data = {}) => {
      addBreadcrumb({
        message: `API call: ${method} ${endpoint}`,
        category: "http",
        level: status >= 400 ? "error" : "info",
        data: {
          endpoint,
          method,
          status,
          duration,
          timestamp: new Date().toISOString(),
          ...data,
        },
      });

      setContext("lastApiCall", {
        endpoint,
        method,
        status,
        duration,
        timestamp: new Date().toISOString(),
        ...data,
      });

      if (status >= 400) {
        logInfo("API call failed", {
          endpoint,
          method,
          status,
          duration,
          ...data,
        });
      }
    },
    []
  );

  // Track performance metrics
  const trackPerformance = useCallback(
    (metricName, value, unit = "ms", data = {}) => {
      addBreadcrumb({
        message: `Performance: ${metricName}`,
        category: "performance",
        level: "info",
        data: {
          metric: metricName,
          value,
          unit,
          timestamp: new Date().toISOString(),
          ...data,
        },
      });

      setContext("performance", {
        [metricName]: {
          value,
          unit,
          timestamp: new Date().toISOString(),
          ...data,
        },
      });

      logInfo("Performance metric tracked", {
        metric: metricName,
        value,
        unit,
        ...data,
      });
    },
    []
  );

  // Track errors with context
  const trackError = useCallback(
    (error, context = {}) => {
      addBreadcrumb({
        message: `Error: ${error.message}`,
        category: "error",
        level: "error",
        data: {
          error: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
          userId: user?.id || user?._id,
          ...context,
        },
      });

      logInfo("Error tracked", {
        error: error.message,
        userId: user?.id || user?._id,
        ...context,
      });
    },
    [user]
  );

  const value = {
    trackPageView,
    trackUserAction,
    trackApiCall,
    trackPerformance,
    trackError,
  };

  return (
    <SentryContext.Provider value={value}>{children}</SentryContext.Provider>
  );
};

/**
 * Hook to use Sentry context
 * @returns {Object} Sentry context value
 */
export const useSentry = () => {
  const context = useContext(SentryContext);
  if (!context) {
    throw new Error("useSentry must be used within a SentryProvider");
  }
  return context;
};

export default SentryContext;
